import { FC } from "react";

import { Add } from "@livechat/design-system-icons";
import { ActionMenu, ActionMenuItem, Button, Icon, SpacingToken } from "@livechat/design-system-react-components";

import { EventPlace, trackEngagement } from "helpers/analytics";
import { FormBlockType, IFormBlock } from "store/entities/contact-forms/interfaces";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import { BLOCK_DEFAULTS, BLOCK_PROPERTIES, ICON_MAP, LABEL_MAP } from "./constants";

type Props = {
  fields: {
    value: IFormBlock[];
    insert: (index: number, block: IFormBlock) => void;
    length?: number;
  };
  onAddField: () => void;
};

export const AddField: FC<Props> = ({ fields, onAddField }) => {
  const hasField = (fieldType: FormBlockType): boolean =>
    fields?.value?.some(({ type }: { type: FormBlockType }) => type === fieldType);

  const options = Object.entries(LABEL_MAP)
    .filter(([key]) => !BLOCK_PROPERTIES[key as FormBlockType]?.hiddenFromAdd)
    .map(([key, value]) => ({
      key,
      element: <ActionMenuItem leftNode={<Icon source={ICON_MAP[key as FormBlockType]} />}>{value}</ActionMenuItem>,
      onClick: (): void => handleOnClick(key as FormBlockType),
      disabled: BLOCK_PROPERTIES[key as FormBlockType]?.unique && hasField(key as FormBlockType),
    }));

  const handleOnClick = (type: FormBlockType): void => {
    if (fields?.length) {
      onAddField();
      trackEngagement("Settings: Contact form field added", EventPlace.Settings, {
        fieldType: LABEL_MAP[type],
      });
      // The last two fields are always submit and options - insert before them
      fields.insert(fields.length - 2, BLOCK_DEFAULTS[type]);
    }
  };

  return (
    <div css={wrapperStyles}>
      <ActionMenu
        placement="bottom-start"
        options={options}
        triggerRenderer={
          <Button icon={<Add />} kind="secondary" css={buttonStyles}>
            Add element
          </Button>
        }
      />
    </div>
  );
};

const wrapperStyles = {
  width: "fit-content",
  "[data-floating-ui-focusable]": { zIndex: StackingContextLevel.ContentOverlayDropdown },
};

const buttonStyles = { marginBottom: withVar(SpacingToken.Spacing2), marginTop: withVar(SpacingToken.Spacing2) };
